import { computed, ref } from 'vue'
import { useMessages } from '@/composables/useMessages'
import { AlertSettings } from '@/@core/fire-risk-settings/domain/entities/AlertSettings'
import { AlertSettingsTableColumnsFactory } from '../factories/AlertSettingsTableColumns.factory'
import { CreateAlertSettingsParser } from '../../../../../../@core/fire-risk-settings/infra/alert-settings-repository-adapter/schemas/CreateAlertSettings.schema'
import { IAlertSettingsRepository } from '../../../../../../@core/fire-risk-settings/domain/repositories/IAlertSettings.repository'
import { CreateAndUpdateAlertSettingsDto } from '../../../../../../@core/fire-risk-settings/domain/dtos/CreateAndUpdateAlertSettings.dto'
import { UpdateAlertSettingsSchema } from '../../../../../../@core/fire-risk-settings/infra/alert-settings-repository-adapter/schemas/UpdateAlertSettings.schema'
import { ExportDataProvider } from '../../../../@shared/providers/ExportData.provider'
import { IMonitoredAreaRepository } from '../../../../../../@core/monitored-area/domain/repositories/IMonitoredArea.repository'
import { MonitoredArea } from '../../../../../../@core/monitored-area/domain/entities/MonitoredArea'
import { useTable } from '../../../../../ui/components/baseTable/composables/Table.composable'

export class AlertSettingsViewModel {
	constructor(
		private _alertSettingsRepository: IAlertSettingsRepository,
		private _monitoredAreaRepository: IMonitoredAreaRepository,
	) {}

	private _addMessage = useMessages().addMessage

	public openForm = ref(false)
	public fetching = ref(false)

	public alertSettingsList = ref<AlertSettings[]>([])

	public showDeletePopup = ref(false)

	MAX_BUFFER_KM = 30

	colunas = computed(() => AlertSettingsTableColumnsFactory.make(this.registry.sortOptions.value))

	isBufferInvalid = computed(() => {
		const buffer = this.alertSettings.value.buffer
		return buffer === 0 || (buffer !== undefined && buffer > this.MAX_BUFFER_KM)
	})

	bufferFieldMessage = computed(() => {
		const buffer = this.alertSettings.value.buffer
		if (buffer === 0) return 'Buffer não pode ser 0'
		if (buffer !== undefined && buffer > this.MAX_BUFFER_KM)
			return `Buffer máximo de ${this.MAX_BUFFER_KM}km`
		return undefined
	})

	public registry = useTable(this.alertSettingsList)

	getAlertSettingsToDelete(): Array<{ label: string; subItems: string[] }> {
		if (!this.alertSettingsList.value) return []
		return this.alertSettingsList.value
			.filter((alertSettings) =>
				this.registry.entriesToDelete.value.includes(alertSettings.id as never),
			)
			.map((alertSettings) => ({
				label: alertSettings.description,
				subItems: [this._formatAffectedUsers(alertSettings.linkedUsersCount)],
			}))
	}

	private _formatAffectedUsers(count: number): string {
		if (count === 0) return 'nenhum usuário afetado'
		if (count === 1) return '1 usuário afetado'
		return `${count} usuários afetados`
	}

	get loggedUserEmail() {
		return localStorage.getItem('email') ?? ''
	}

	kebabOptions() {
		return [
			{
				icon: 'ic:round-edit',
				event: 'editar',
				name: 'Editar',
			},
		]
	}

	public farms = ref<MonitoredArea[]>([])

	public monitoredAreaOptions = computed(() =>
		this.farms.value.map((area) => {
			return { label: area.name, value: area.id }
		}),
	)

	private _initAlertSettingsState = {
		id: undefined,
		description: '',
		buffer: undefined as number | undefined,
		minDetectionConfidence: 0,
		maxDetectionConfidence: 100,
		monitoredAreas: [] as string[],
	}

	public alertSettings = ref({
		...this._initAlertSettingsState,
	})

	_mapAlertSettingsToCreate(): CreateAndUpdateAlertSettingsDto | undefined {
		const result = CreateAlertSettingsParser.safeParse(this.alertSettings.value)

		if (!result.success) {
			this._onErrorCreateUpdateAlertSettings(result.error.issues.at(0)!.message)
			return undefined
		}

		return result.data
	}

	_mapAlertSettingsToUpdate(): CreateAndUpdateAlertSettingsDto | undefined {
		const result = UpdateAlertSettingsSchema.safeParse(this.alertSettings.value)

		if (!result.success) {
			this._onErrorCreateUpdateAlertSettings(result.error.issues.at(0)!.message)
			return undefined
		}

		return result.data
	}

	async createAlertSettings() {
		this.fetching.value = true
		try {
			const payload = this._mapAlertSettingsToCreate()
			if (!payload) return

			await this._alertSettingsRepository.create(payload)
			this._onSuccessCreateAlertSettings()
		} catch (error) {
			this._onErrorCreateUpdateAlertSettings((error as Error).message)
		} finally {
			this.fetching.value = false
		}
	}

	async updateAlertSettings() {
		this.fetching.value = true
		try {
			const payload = this._mapAlertSettingsToUpdate()
			if (!payload) return
			await this._alertSettingsRepository.update(payload)
			this._onSuccessUpdateAlertSettings()
		} catch (error) {
			this._onErrorCreateUpdateAlertSettings((error as Error).message)
		} finally {
			this.fetching.value = false
		}
	}

	loadAlertSettings(alertSettingsId: string) {
		const alertSettings = this.alertSettingsList.value?.find(
			(alertSettings) => alertSettings.id === alertSettingsId,
		)
		if (AlertSettings) {
			this.alertSettings.value = { ...alertSettings } as any
			this.alertSettings.value.monitoredAreas = alertSettings!.monitoredAreas.map(
				(area) => area.id,
			) as string[]
			this.openForm.value = true
		} else {
			this._addMessage({
				description: 'Erro ao recuperar dados de configuração de alerta',
				type: 'error',
			})
		}
	}

	async getAlertSettings() {
		try {
			const customerId = localStorage.getItem('customerId')
			const response = await this._alertSettingsRepository.getAll(customerId!)
			this.alertSettingsList.value = response
		} catch {
			this._addMessage({ description: 'Erro ao buscar configurações de alerta', type: 'error' })
		}
	}

	async getMonitoredAreas() {
		await this._getUnsettedMonitoredAreas()
		await this._getSettedMonitoredAreas()
	}

	private async _getUnsettedMonitoredAreas() {
		try {
			const customerId = localStorage.getItem('customerId')
			const response = await this._monitoredAreaRepository.getUnsetted(customerId!)
			this.farms.value = response
		} catch {
			this._addMessage({ description: 'Erro ao buscar áreas monitoradas', type: 'error' })
		}
	}

	private async _getSettedMonitoredAreas() {
		try {
			const customerId = localStorage.getItem('customerId')
			const response = await this._monitoredAreaRepository.getSetted(customerId!)
			this.farms.value.push(
				...response.filter((area) => this.alertSettings.value.monitoredAreas.includes(area.id)),
			)
		} catch {
			this._addMessage({ description: 'Erro ao buscar áreas monitoradas', type: 'error' })
		}
	}

	async deleteAlertSettings() {
		try {
			await this._alertSettingsRepository.delete(this.registry.entriesToDelete.value)

			this.fetching.value = false
			this.showDeletePopup.value = false
			this.registry.entriesToDelete.value = []
			this.getAlertSettings()
			this._addMessage({
				description: 'Configuração de alerta excluída com sucesso!',
				type: 'info',
			})
		} catch {
			this._addMessage({ description: 'Erro ao excluir configurações de alerta', type: 'error' })
		}
	}

	private _onSuccessUpdateAlertSettings() {
		this.openForm.value = false
		this.getAlertSettings()
		this._addMessage({
			description: 'Configuração de alerta editada com sucesso!',
			type: 'success',
		})
	}

	private _onSuccessCreateAlertSettings() {
		this.openForm.value = false
		this.getAlertSettings()
		this._addMessage({
			description: 'Configuração de alerta cadastrada com sucesso!',
			type: 'success',
		})
	}

	private _onErrorCreateUpdateAlertSettings(errorMessage: string) {
		this._addMessage({
			description: errorMessage,
			type: 'error',
		})
	}

	public resetAlertSettingsState() {
		this.farms.value = []
		this.alertSettings.value = { ...this._initAlertSettingsState }
	}

	get isFormValid() {
		const isDescriptionValid = this.alertSettings.value.description
		const hasFarmChosen = this.alertSettings.value.monitoredAreas.length > 0
		const buffer = this.alertSettings.value.buffer
		const isBufferValid = buffer !== undefined && buffer > 0 && buffer <= this.MAX_BUFFER_KM
		const isMinDetectionConfidenceValid = this.alertSettings.value.minDetectionConfidence
			? this.alertSettings.value.minDetectionConfidence > 0 &&
				this.alertSettings.value.minDetectionConfidence <= 100
			: true
		const isMaxDetectionConfidenceValid = this.alertSettings.value.maxDetectionConfidence
			? this.alertSettings.value.maxDetectionConfidence > 0 &&
				this.alertSettings.value.maxDetectionConfidence <= 100
			: true

		return (
			isDescriptionValid &&
			hasFarmChosen &&
			isBufferValid &&
			isMinDetectionConfidenceValid &&
			isMaxDetectionConfidenceValid
		)
	}

	exportDataTable() {
		const columns = [
			{ title: 'Codigo', key: 'code' },
			{ title: 'Descricao', key: 'description' },
			{ title: 'Fazenda-setor', key: 'areas' },
			{ title: 'Buffer (km)', key: 'buffer' },
			{ title: 'Confiança mínima da detecção', key: 'minDetectionConfidence' },
		]

		ExportDataProvider.downloadCSV(
			this.alertSettingsList.value!,
			columns,
			`Configurações de Alerta ${new Date().toISOString()}`,
		)
	}
}
